/* eslint-disable */
import { useEffect, useState, useCallback } from "react";
import {
  ArrowLeft,
  ArrowRight,
  Folder,
  FolderPlus,
  Pencil,
  Search,
  Trash2,
  X,
} from "lucide-react";
import { docsApi, categoryMeta } from "./api";
import DocumentCard from "./DocumentCard";
import BundleRow from "./BundleRow";
import { ViewToggle, Pager, useViewMode, DOCS_PAGE_SIZE } from "./DocsControls";
import useDebouncedValue from "../utils/useDebouncedValue";

export default function CategoryDetail({ category, isAdmin, onBack, onChange, initialBundle = null }) {
  const meta = categoryMeta(category);
  const [docs, setDocs] = useState([]);
  const [bundles, setBundles] = useState([]);
  // Seed straight into a bundle when opened from a bundle tile elsewhere
  // (e.g. the Most Viewed list). Backing out returns to the category view.
  const [openBundle, setOpenBundle] = useState(initialBundle);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState("");
  const [sort, setSort] = useState("newest");
  const [creatingBundle, setCreatingBundle] = useState(false);
  const [newBundleName, setNewBundleName] = useState("");
  const [editBundle, setEditBundle] = useState(null);
  const [editBundleName, setEditBundleName] = useState("");
  const [editBundleSaving, setEditBundleSaving] = useState(false);
  const [viewMode, setViewMode] = useViewMode();
  const [page, setPage] = useState(1);
  const [total, setTotal] = useState(0);
  const debouncedSearch = useDebouncedValue(search, 300);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      // Only the "loose" docs are listed at this level; bundled docs
      // appear after the user opens a bundle tile.
      const [docsData, bundlesData] = await Promise.all([
        docsApi.list({
          category,
          sort,
          search: debouncedSearch || undefined,
          limit: DOCS_PAGE_SIZE,
          offset: (page - 1) * DOCS_PAGE_SIZE,
          unbundled_only: true,
        }),
        docsApi.listBundles(category),
      ]);
      setDocs(docsData.documents || []);
      setBundles(bundlesData.bundles || []);
      setTotal(docsData.total || 0);
    } catch (e) {
      console.error(e);
    } finally {
      setLoading(false);
    }
  }, [category, sort, debouncedSearch, page]);

  // Reset to the first page when the category, sort, or search changes.
  useEffect(() => {
    setPage(1);
  }, [category, sort, debouncedSearch]);

  useEffect(() => {
    load();
  }, [load]);

  const createBundle = async () => {
    const name = newBundleName.trim();
    if (!name) return;
    try {
      await docsApi.createBundle({ name, category });
      setNewBundleName("");
      setCreatingBundle(false);
      load();
    } catch (e) {
      alert(e.message || "Could not create bundle");
    }
  };

  const openRenameBundle = (b) => {
    setEditBundle(b);
    setEditBundleName(b.name);
  };

  const saveRenameBundle = async () => {
    const name = editBundleName.trim();
    if (!name || !editBundle) return;
    setEditBundleSaving(true);
    try {
      await docsApi.patchBundle(editBundle.id, { name });
      setEditBundle(null);
      load();
    } catch (e) {
      alert(e.message || "Could not rename bundle");
    } finally {
      setEditBundleSaving(false);
    }
  };

  const deleteBundleHandler = async (b) => {
    if (!window.confirm(`Delete folder "${b.name}"? Documents inside will not be deleted.`)) return;
    try {
      await docsApi.deleteBundle(b.id);
      load();
    } catch (e) {
      alert(e.message || "Could not delete folder");
    }
  };

  if (openBundle) {
    return (
      <BundleDetail
        bundleId={openBundle.id}
        bundleName={openBundle.name}
        meta={meta}
        category={category}
        isAdmin={isAdmin}
        onBack={() => {
          setOpenBundle(null);
          load();
        }}
        onChange={onChange}
      />
    );
  }

  return (
    <div className="docs-wrap">
      <div className="docs-toolbar">
        <div className="docs-breadcrumb">
          <button onClick={onBack}>Home</button>
          <span>›</span>
          <button onClick={onBack}>Documents</button>
          <span>›</span>
          <span className="docs-breadcrumb-current">{meta.label}</span>
        </div>

        <div className="docs-cat-header">
          <button className="docs-back-btn" onClick={onBack} aria-label="Back">
            <ArrowLeft size={18} />
          </button>
          <div
            className="docs-cat-header-icon"
            style={{ background: meta.bg, color: meta.color }}
          >
            {meta.Icon && <meta.Icon size={22} strokeWidth={1.8} />}
          </div>
          <div className="docs-cat-header-text">
            <h1>{meta.label}</h1>
            <p>{total} document{total === 1 ? "" : "s"}</p>
          </div>
          <div className="docs-cat-header-sort">
            <ViewToggle mode={viewMode} onChange={setViewMode} />
            <select value={sort} onChange={(e) => setSort(e.target.value)}>
              <option value="newest">Newest First</option>
              <option value="oldest">Oldest First</option>
              <option value="mostViewed">Most Viewed</option>
            </select>
          </div>
        </div>

        <div className="docs-search">
          <span className="docs-search-icon">
            <Search size={16} />
          </span>
          <input
            type="text"
            placeholder={`Search within ${meta.label}…`}
            value={search}
            onChange={(e) => setSearch(e.target.value)}
          />
        </div>
      </div>

      <div className="docs-content">
        {/* New-bundle affordance + inline create */}
        <div className="docs-section-head">
          <h3>Folders &amp; documents</h3>
          {!creatingBundle && (
            <button
              className="docs-bundle-create-btn"
              onClick={() => setCreatingBundle(true)}
            >
              <FolderPlus size={13} /> New bundle
            </button>
          )}
        </div>
        {creatingBundle && (
          <div className="docs-bundle-create-row">
            <input
              autoFocus
              type="text"
              placeholder='e.g. "Q4 2026 Invoices"'
              value={newBundleName}
              onChange={(e) => setNewBundleName(e.target.value)}
              onKeyDown={(e) => {
                if (e.key === "Enter") createBundle();
                if (e.key === "Escape") {
                  setCreatingBundle(false);
                  setNewBundleName("");
                }
              }}
            />
            <button
              className="docs-btn-primary docs-btn-auto"
              onClick={createBundle}
              disabled={!newBundleName.trim()}
            >
              Create
            </button>
            <button
              className="docs-bundle-create-cancel"
              onClick={() => {
                setCreatingBundle(false);
                setNewBundleName("");
              }}
            >
              <X size={14} />
            </button>
          </div>
        )}
        {editBundle && (
          <div className="docs-bundle-create-row">
            <input
              autoFocus
              type="text"
              placeholder="Folder name"
              value={editBundleName}
              onChange={(e) => setEditBundleName(e.target.value)}
              onKeyDown={(e) => {
                if (e.key === "Enter") saveRenameBundle();
                if (e.key === "Escape") setEditBundle(null);
              }}
            />
            <button
              className="docs-btn-primary docs-btn-auto"
              onClick={saveRenameBundle}
              disabled={!editBundleName.trim() || editBundleSaving}
            >
              {editBundleSaving ? "Saving…" : "Save"}
            </button>
            <button
              className="docs-bundle-create-cancel"
              onClick={() => setEditBundle(null)}
            >
              <X size={14} />
            </button>
          </div>
        )}

        {loading && docs.length === 0 && bundles.length === 0 ? (
          // Show the Loading state only on the *first* fetch. When a
          // background refresh fires (e.g. post-comment), keeping the
          // list mounted preserves DocumentCard's local viewerOpen state
          // so the PDF viewer + comment modal stay open.
          <div className="docs-empty">Loading…</div>
        ) : docs.length === 0 && bundles.length === 0 ? (
          <div className="docs-empty">
            <div
              className="docs-empty-icon"
              style={{ background: meta.bg, color: meta.color }}
            >
              {meta.Icon && <meta.Icon size={28} strokeWidth={1.8} />}
            </div>
            <h3>No documents found</h3>
            <p>
              {search
                ? "Try adjusting your search"
                : `No documents in ${meta.label} yet`}
            </p>
          </div>
        ) : (
          // One listing: bundles are NOT pinned on top — a folder sorts
          // among loose documents by when it was created (bundle.created_at
          // vs document.uploaded_at), honouring the chosen sort. "Most
          // viewed" keeps the view-ranked docs first with folders after,
          // since bundles have no view count to rank by.
          <>
            <div className={viewMode === "grid" ? "docs-tilegrid" : "docs-rowlist"}>
              {(() => {
                const bundleItems = bundles.map((b) => ({
                  kind: "bundle",
                  key: `b-${b.id}`,
                  ts: b.created_at ? Date.parse(b.created_at) : 0,
                  data: b,
                }));
                const docItems = docs.map((d) => ({
                  kind: "doc",
                  key: `d-${d.id}`,
                  ts: d.uploaded_at ? Date.parse(d.uploaded_at) : 0,
                  data: d,
                }));
                let items;
                if (sort === "mostViewed") {
                  items = [...docItems, ...bundleItems];
                } else {
                  items = [...bundleItems, ...docItems].sort((a, b) =>
                    sort === "oldest" ? a.ts - b.ts : b.ts - a.ts
                  );
                }
                return items.map((it) =>
                  it.kind === "bundle" ? (
                    <BundleRow
                      key={it.key}
                      bundle={it.data}
                      showCategory={false}
                      variant={viewMode === "grid" ? "tile" : "row"}
                      onOpen={setOpenBundle}
                      onRename={isAdmin ? openRenameBundle : undefined}
                      onDelete={isAdmin ? deleteBundleHandler : undefined}
                    />
                  ) : (
                    <DocumentCard
                      key={it.key}
                      doc={it.data}
                      isAdmin={isAdmin}
                      onChange={() => {
                        load();
                        onChange && onChange();
                      }}
                      showCategory={false}
                      variant={viewMode === "grid" ? "tile" : "row"}
                    />
                  )
                );
              })()}
            </div>
            <Pager
              page={page}
              pageSize={DOCS_PAGE_SIZE}
              total={total}
              onPage={setPage}
            />
          </>
        )}
      </div>
    </div>
  );
}

function BundleDetail({
  bundleId,
  bundleName,
  meta,
  category,
  isAdmin,
  onBack,
  onChange,
}) {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [viewMode, setViewMode] = useViewMode();
  const [page, setPage] = useState(1);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const d = await docsApi.getBundle(bundleId);
      setData(d);
    } catch (e) {
      console.error(e);
    } finally {
      setLoading(false);
    }
  }, [bundleId]);

  useEffect(() => {
    load();
  }, [load]);

  return (
    <div className="docs-wrap">
      <div className="docs-toolbar">
        <div className="docs-breadcrumb">
          <button onClick={onBack}>← {meta.label}</button>
          <span>›</span>
          <span className="docs-breadcrumb-current">{bundleName}</span>
        </div>
        <div className="docs-cat-header">
          <button className="docs-back-btn" onClick={onBack} aria-label="Back">
            <ArrowLeft size={18} />
          </button>
          <div
            className="docs-cat-header-icon"
            style={{ background: meta.bg, color: meta.color }}
          >
            <Folder size={22} strokeWidth={1.8} />
          </div>
          <div className="docs-cat-header-text">
            <h1>{data?.name || bundleName}</h1>
            <p>
              {data?.document_count || 0} document
              {data?.document_count === 1 ? "" : "s"} in this bundle
            </p>
          </div>
        </div>
        {data?.description && (
          <p className="docs-bundle-description">{data.description}</p>
        )}
      </div>

      <div className="docs-content">
        {loading && !data ? (
          <div className="docs-empty">Loading…</div>
        ) : !data || data.documents.length === 0 ? (
          <div className="docs-empty">
            <div
              className="docs-empty-icon"
              style={{ background: meta.bg, color: meta.color }}
            >
              <Folder size={28} strokeWidth={1.8} />
            </div>
            <h3>This bundle is empty</h3>
            <p>Upload a document and choose this bundle to add it here.</p>
          </div>
        ) : (
          <>
            <div className="docs-section-head">
              <h3>Documents</h3>
              <ViewToggle mode={viewMode} onChange={setViewMode} />
            </div>
            <div className={viewMode === "grid" ? "docs-tilegrid" : "docs-rowlist"}>
              {data.documents
                .slice((page - 1) * DOCS_PAGE_SIZE, page * DOCS_PAGE_SIZE)
                .map((d) => (
                  <DocumentCard
                    key={d.id}
                    doc={d}
                    isAdmin={isAdmin}
                    onChange={() => {
                      load();
                      onChange && onChange();
                    }}
                    showCategory={false}
                    variant={viewMode === "grid" ? "tile" : "row"}
                  />
                ))}
            </div>
            <Pager
              page={page}
              pageSize={DOCS_PAGE_SIZE}
              total={data.documents.length}
              onPage={setPage}
            />
          </>
        )}
      </div>
    </div>
  );
}
